type ErrorRecord = Record<string, unknown>;

const NETWORK_AXIOS_CODES = [
  'econnaborted',
  'econnrefused',
  'econnreset',
  'etimedout',
  'err_network',
  'err_internet_disconnected',
  'enotfound',
  'enetunreach',
  'ehostunreach',
];

// True when the browser reports no connectivity
export const isBrowserOffline = (): boolean =>
  typeof navigator !== 'undefined' && !navigator.onLine;

const NETWORK_MESSAGE_PATTERNS = [
  'network',
  'fetch',
  'connection',
  'timeout',
  'err_network_changed',
  'connection_refused',
  'err_connection_refused',
  'failed to fetch',
  'network request failed',
  'networkerror',
];

const toErrorRecord = (error: unknown): ErrorRecord =>
  error !== null && typeof error === 'object' ? (error as ErrorRecord) : {};

/**
 * Extracts the HTTP status code from an Axios-like error, if present.
 *
 * Checks both `error.response.status` and `error.status` to cover
 * different error shapes across Axios versions and custom wrappers.
 */
export const getHttpErrorStatus = (error: unknown): number | undefined => {
  const record = toErrorRecord(error);
  const response = record.response as { status?: number } | undefined;
  const status = response?.status ?? (record.status as number | undefined);

  return typeof status === 'number' && status > 0 ? status : undefined;
};

/**
 * Returns true when a request failed before receiving an HTTP response.
 *
 * First checks for known Axios network error codes, then falls back to
 * message pattern matching for errors from other HTTP clients. Errors
 * that have an HTTP status are never treated as transport failures.
 */
export const isNetworkTransportError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const record = toErrorRecord(error);
  const code = (record.code as string | undefined)?.toLowerCase();

  if (code && NETWORK_AXIOS_CODES.includes(code)) {
    return true;
  }

  if (getHttpErrorStatus(error) !== undefined) {
    return false;
  }

  const message = (
    (record.message as string | undefined) || String(error)
  ).toLowerCase();

  return NETWORK_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern));
};

/**
 * Returns true when a fetch failure should be treated as a network error.
 *
 * Combines browser offline detection with transport error classification
 * so callers have a single check for both offline and connectivity failure cases.
 */
export const shouldTreatFetchFailureAsNetwork = (error: unknown): boolean =>
  isBrowserOffline() || isNetworkTransportError(error);
